import {DISPLAY_CREDIT_UNITS} from '../../utils/displayCredits';
import {ApiError, CREDIT_CHECK_PENDING} from './errors';
import {studioText} from './studioText';
import type {CreditCheckReason, JobView} from './types';

/** Internal accounting in beta never blocks an author's Studio actions. */
export const hasCreditHold = (view: JobView | null | undefined) =>
  view?.billing?.enforceAiCredits !== false &&
  !!view?.billing?.reconciliationRequired;

/**
 * A pending credit check: the AI credits a stopped generation holds have not settled yet, so
 * the job's AI actions wait. The author can add AI credits when the workspace ran out (or ask
 * an administrator to, without Manage settings), and ask Studio to check again (StoryboardStore
 * and ReviewStore `checkCredits`).
 */
export interface CreditCheck {
  /** Why it waits; null when the server does not say (older servers). */
  reason: CreditCheckReason | null;
  /** What the author reads: why AI actions wait and what unlocks them. */
  text: string;
  /** Adding AI credits may unlock it, so the page links to where the workspace buys them. */
  addCredits: boolean;
}

/**
 * The job's pending credit check, or null when its AI credits are settled. `canAddCredits`:
 * the author may buy AI credits (see `useAddCreditsPath`); otherwise the text asks them to go
 * to their workspace administrator.
 */
export function creditCheck(
  view: JobView | null | undefined,
  canAddCredits = false,
): CreditCheck | null {
  const billing = view?.billing;
  if (!billing || !hasCreditHold(view)) return null;
  if (billing.creditCheckReason === 'receipt')
    return {
      reason: 'receipt',
      text: studioText.creditCheckReceipt,
      addCredits: false,
    };
  // A cost that stayed unknown waits for support to record it: adding credits cannot help.
  if (billing.creditCheckReason === 'review')
    return {
      reason: 'review',
      text: studioText.creditCheckReview,
      addCredits: false,
    };
  if (billing.creditCheckReason === 'credits') {
    const needed = billing.creditsNeeded;
    // None to add: the workspace has enough now (e.g. just topped up), and only the settlement
    // itself is left, which Studio asks for as soon as the page sees it.
    if (needed === 0)
      return {
        reason: 'credits',
        text: studioText.creditCheckCovered,
        addCredits: false,
      };
    // The server counts the backend's units; the app shows whole credits, rounded up.
    const credits =
      typeof needed === 'number' && needed > 0
        ? studioText.aiCredits(Math.ceil(needed / DISPLAY_CREDIT_UNITS))
        : null;
    return {
      reason: 'credits',
      text: canAddCredits
        ? studioText.creditCheckCredits(credits)
        : studioText.creditCheckAskCredits(credits),
      addCredits: canAddCredits,
    };
  }
  // The server does not say why: adding credits helps when the workspace ran out.
  return {
    reason: null,
    text: canAddCredits
      ? CREDIT_CHECK_PENDING
      : studioText.creditCheckPendingAsk,
    addCredits: canAddCredits,
  };
}

/**
 * What the page says when checking again left the credits unsettled, by why they wait:
 * checking again in a minute helps only while the cost is confirmed or the settlement itself
 * is left.
 */
export function creditsNotSettled(view: JobView | null | undefined): string {
  const billing = view?.billing;
  if (billing?.creditCheckReason === 'review')
    return studioText.creditsNotSettledReview;
  if (billing?.creditCheckReason === 'credits' && billing.creditsNeeded !== 0)
    return studioText.creditsNotSettledCredits;
  return studioText.creditsNotSettled;
}

/**
 * A server without the credit check (an older one, or Studio on its own) answers it with a
 * bare 404 or 405: the page reads the job again, as after a check that did not settle.
 */
export const cannotCheckCredits = (error: unknown) =>
  error instanceof ApiError &&
  (error.status === 405 || (error.status === 404 && !error.fromServer));
